import {
  assessJob,
  describeMissedBriefly,
  humanBytes,
  humanDuration,
  humanTime,
  INHERIT_PLAN_OVERRIDE,
  machineNamesForRepo,
  MAX_STALE_AFTER_DAYS,
  MIN_STALE_AFTER_DAYS,
  sharedByComputersSentence,
  shortId,
  type BackupJob,
  type PlanNotificationOverride,
  type PortalOverview,
} from "@bigbear/shared";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { HealthBadge } from "../components/HealthBadge";
import { JobActionsMenu } from "../components/JobActionsMenu";

interface Props {
  overview: PortalOverview;
  nowUtc?: number;
  /** Absent in tests that render this page with nothing to act through. */
  onClearJobHistory?: (machineId: string, jobId: string) => Promise<void>;
  /**
   * The "Alerts for this plan" card, bound to the API the same way
   * `onClearJobHistory` is — all three come from `Dashboard`, and all three
   * are absent together in tests that have nothing for the card to act
   * through, so the card itself does not render without them.
   */
  onLoadPlanAlerts?: (planId: string) => Promise<PlanNotificationOverride | null>;
  onSavePlanAlerts?: (
    planId: string,
    override: PlanNotificationOverride,
  ) => Promise<PlanNotificationOverride>;
  onClearPlanAlerts?: (planId: string) => Promise<void>;
}

export function JobDetail({
  overview,
  nowUtc,
  onClearJobHistory,
  onLoadPlanAlerts,
  onSavePlanAlerts,
  onClearPlanAlerts,
}: Props) {
  const { machineId = "", jobId = "" } = useParams();
  const now = nowUtc ?? Math.floor(Date.now() / 1000);

  // Matched on both parts: a job id alone is ambiguous, since several machines
  // commonly run one called "photos".
  const job = overview.jobs.find((j) => j.id === jobId && j.machineId === machineId);

  if (!job) {
    return (
      <div className="empty">
        <h2>No such job</h2>
        <p className="muted">
          Nothing named <span className="mono">{jobId}</span> is configured on{" "}
          <span className="mono">{machineId}</span>.
        </p>
        <p>
          <Link to="..">Back to the overview</Link>
        </p>
      </div>
    );
  }

  const assessment = assessJob(job, overview.snapshots, now);
  const target = targetOf(overview, job);
  const shared = sharedSentenceFor(overview, job);
  const runs = overview.snapshots
    .filter((s) => s.jobId === job.id && s.machineId === job.machineId)
    .toSorted((a, b) => b.finishedUtc - a.finishedUtc);

  return (
    <>
      <p className="crumb">
        <Link to="..">Overview</Link> ›{" "}
        {overview.machines.find((m) => m.id === machineId)?.name ?? machineId}
      </p>

      <section className="panel">
        <div className="job-header">
          <div>
            <h2>{job.name}</h2>
            <p className="muted">{job.sourcePaths.join(", ") || "No sources recorded."}</p>
            {target && (
              <p className="muted job-header__target">
                {/* The arrow, rather than a "Target:" label, because the line
                    above it is already the source and the pair reads as one
                    sentence: these folders go there. */}
                <span aria-hidden="true">→ </span>
                <span className="sr-only">Backed up to: </span>
                {target}
                {shared && ` ${shared}`}
              </p>
            )}
          </div>
          <HealthBadge health={assessment.health} />
        </div>
        <p>{assessment.reason}</p>
      </section>

      {onLoadPlanAlerts && onSavePlanAlerts && onClearPlanAlerts && (
        <PlanAlertsCard
          planId={job.id}
          onLoad={onLoadPlanAlerts}
          onSave={onSavePlanAlerts}
          onClear={onClearPlanAlerts}
        />
      )}

      <section className="panel">
        <div className="panel__header">
          <h2>Run history</h2>
          {onClearJobHistory && (
            <JobActionsMenu
              machineId={job.machineId}
              jobId={job.id}
              label={job.name}
              onClearHistory={onClearJobHistory}
            />
          )}
        </div>
        {runs.length === 0 ? (
          <p className="muted">This job has not run yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="runs">
              <thead>
                <tr>
                  <th scope="col">Finished</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">Files</th>
                  <th scope="col">Size</th>
                  <th scope="col">Stored</th>
                  <th scope="col">Missed</th>
                  <th scope="col">Took</th>
                  <th scope="col">Snapshot</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const missed = describeMissedBriefly(run.missedFiles, run.neverCapturedFiles);
                  return (
                    <tr key={run.id} className={run.status === "complete" ? "" : "is-bad"}>
                      <td>{humanTime(run.finishedUtc)}</td>
                      <td>{run.status}</td>
                      <td className="num">{run.files.toLocaleString()}</td>
                      <td className="num">{humanBytes(run.bytes)}</td>
                      <td className="num">{humanBytes(run.newBytes)}</td>
                      <td className={missed ? "is-attention" : ""}>{missed ?? "—"}</td>
                      <td className="num">
                        {humanDuration(run.finishedUtc - run.startedUtc)}
                      </td>
                      <td className="mono">{shortId(run.id, 16)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * A plan's own alert settings, layered over the account-wide ones from
 * `/notifications` — inherit each field, or pin it on or off (and pin its own
 * staleness grace) for this plan alone. `planId` is `job.id`: a backup plan's
 * id is unique per account, unlike a job id (which, per `JobDetail` itself,
 * two machines commonly share), so the plan this card edits is the one this
 * page is already showing.
 */
function PlanAlertsCard({
  planId,
  onLoad,
  onSave,
  onClear,
}: {
  planId: string;
  onLoad: (planId: string) => Promise<PlanNotificationOverride | null>;
  onSave: (
    planId: string,
    override: PlanNotificationOverride,
  ) => Promise<PlanNotificationOverride>;
  onClear: (planId: string) => Promise<void>;
}) {
  const [override, setOverride] = useState<PlanNotificationOverride | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    onLoad(planId)
      .then((loaded) => {
        // `null` from the API and `INHERIT_PLAN_OVERRIDE` mean the same
        // thing — "nothing pinned for this plan" — but the form needs a
        // concrete object with fields to bind its selects to either way.
        if (active) setOverride(loaded ?? INHERIT_PLAN_OVERRIDE);
      })
      .catch((cause: unknown) => {
        if (active) setLoadError(explain(cause));
      });
    return () => {
      active = false;
    };
  }, [planId, onLoad]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !override) return;

    setBusy(true);
    setSaveError(null);
    try {
      const result = await onSave(planId, override);
      setOverride(result);
      setSaved(true);
    } catch (cause) {
      setSaveError(explain(cause));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    if (busy) return;

    setBusy(true);
    setSaveError(null);
    try {
      await onClear(planId);
      setOverride(INHERIT_PLAN_OVERRIDE);
      setSaved(true);
    } catch (cause) {
      setSaveError(explain(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel__header">
        <h2>Alerts for this plan</h2>
      </div>

      {loadError && (
        <p className="auth__error" role="alert">
          {loadError}
        </p>
      )}

      {!override && !loadError && <p className="muted">Loading…</p>}

      {override && (
        <form onSubmit={submit} noValidate>
          {saved && (
            <p className="auth__notice" role="status">
              Saved.
            </p>
          )}
          {saveError && (
            <p className="auth__error" role="alert">
              {saveError}
            </p>
          )}

          <div className="field">
            <label htmlFor="plan-alert-on-failure">Alert on failure</label>
            <select
              id="plan-alert-on-failure"
              value={triStateOption(override.alertOnFailure)}
              onChange={(event) => {
                const alertOnFailure = parseTriStateOption(event.target.value);
                setOverride((prev) => (prev ? { ...prev, alertOnFailure } : prev));
                setSaved(false);
              }}
            >
              <option value="inherit">Inherit</option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="plan-alert-on-success">Alert on success</label>
            <select
              id="plan-alert-on-success"
              value={triStateOption(override.alertOnSuccess)}
              onChange={(event) => {
                const alertOnSuccess = parseTriStateOption(event.target.value);
                setOverride((prev) => (prev ? { ...prev, alertOnSuccess } : prev));
                setSaved(false);
              }}
            >
              <option value="inherit">Inherit</option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
            <span className="field__hint muted">
              &quot;Inherit&quot; follows your account-wide{" "}
              <Link to="/notifications">notification settings</Link>. Success alerts reach instant
              Slack, Discord and webhook channels only — email and any channel set to a digest
              report failures and staleness alone.
            </span>
          </div>

          <div className="field">
            <label htmlFor="plan-stale-after-days">Warn after N days without success</label>
            <input
              id="plan-stale-after-days"
              type="number"
              min={MIN_STALE_AFTER_DAYS}
              max={MAX_STALE_AFTER_DAYS}
              placeholder="Inherit"
              value={override.staleAfterDays ?? ""}
              onChange={(event) => {
                const raw = event.target.value;
                const staleAfterDays = raw === "" ? null : Number(raw);
                setOverride((prev) => (prev ? { ...prev, staleAfterDays } : prev));
                setSaved(false);
              }}
            />
            <span className="field__hint muted">Leave blank to inherit the account-wide grace.</span>
          </div>

          <div className="job-detail-alerts__actions">
            <button type="submit" className="button button--primary" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="button" onClick={clear} disabled={busy}>
              Clear override
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function triStateOption(value: boolean | null): "inherit" | "on" | "off" {
  return value === null ? "inherit" : value ? "on" : "off";
}

function parseTriStateOption(value: string): boolean | null {
  return value === "inherit" ? null : value === "on";
}

function explain(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Could not reach the server. Please try again.";
}

/**
 * Where this job writes, as one line, or null if the portal cannot say.
 *
 * Three answers rather than two, because "we do not know" and "we know only
 * the name of it" are different states with different right renderings:
 *
 * - the destination and the label together, which is the normal case;
 * - the label alone, when the machine reporting runs a desktop from before
 *   destinations were reported — the label is what that user has always seen
 *   here, and showing it beats showing a gap;
 * - nothing, when the job names no repository or names one this account has
 *   no record of. A soft-deleted repository is the ordinary way in: the job
 *   outlives it until the purge window closes, and inventing a line for it
 *   would be inventing the one fact this line exists to state.
 */
function targetOf(overview: PortalOverview, job: BackupJob): string | null {
  const repository = overview.repositories.find((r) => r.id === job.repositoryId);
  if (!repository) return null;

  return repository.destination
    ? `${repository.destination} (${repository.label})`
    : repository.label;
}

/**
 * "Shared by 2 computers: Desk PC and Laptop.", when the job's repository is
 * one two or more devices report the same `repoId` for — null otherwise,
 * including when the repository has no `repoId` at all (`#308`'s "not opened
 * since" case, which this page can no more call unshared than shared).
 */
function sharedSentenceFor(overview: PortalOverview, job: BackupJob): string | null {
  const repository = overview.repositories.find((r) => r.id === job.repositoryId);
  if (!repository?.repoId) return null;

  const names = machineNamesForRepo(overview.repositories, overview.machines, repository.repoId);
  return sharedByComputersSentence(names);
}
